import React from "react";
import { useI18n } from "@/contexts/I18nContext";
import { Globe2, Anchor, Award, ShieldCheck } from "lucide-react";
import Leadership from "@/components/Leadership";

const About = () => {
  const { t, lang } = useI18n();

  const content = {
    en: {
      eyebrow: "About Nakia",
      title: "International Trade, Built on Trust.",
      lead: "Nakia Global Sejahtera connects Indonesia, Türkiye, Singapore and Southeast Asia with global markets. We serve serious B2B buyers, suppliers, and institutional partners with transparent, regulation-compliant trade solutions.",
      missionTitle: "Our Mission",
      mission: "To deliver premium, traceable, and certified commodities and consumer goods across borders—with documentation that institutional buyers and regulators trust.",
      visionTitle: "Our Vision",
      vision: "To become the most reliable trade bridge between Southeast Asia, the Middle East, and Europe—turning complex import-export operations into elegant, predictable workflows.",
      values: [
        { i: ShieldCheck, t: "Trust", d: "Verified suppliers, audited capacity, full documentation." },
        { i: Globe2, t: "Global Reach", d: "Active networks across 35+ countries and major ports." },
        { i: Award, t: "Premium Quality", d: "Grade-A commodities and certified manufactured goods only." },
        { i: Anchor, t: "Logistics Mastery", d: "Sea, air and multimodal coordination, end to end." },
      ],
    },
    tr: {
      eyebrow: "Hakkımızda",
      title: "Güvene Dayalı Uluslararası Ticaret.",
      lead: "Nakia Global Sejahtera; Endonezya, Türkiye, Singapur ve Güneydoğu Asya'yı küresel pazarlara bağlar. Şeffaf, mevzuata uygun ticaret çözümleri ile ciddi B2B alıcılar, tedarikçiler ve kurumsal ortaklara hizmet veriyoruz.",
      missionTitle: "Misyonumuz",
      mission: "Premium, izlenebilir ve sertifikalı emtia ve tüketici ürünlerini sınırlar arasında, kurumsal alıcıların ve düzenleyicilerin güvendiği belgelerle teslim etmek.",
      visionTitle: "Vizyonumuz",
      vision: "Güneydoğu Asya, Orta Doğu ve Avrupa arasında en güvenilir ticaret köprüsü olmak.",
      values: [
        { i: ShieldCheck, t: "Güven", d: "Doğrulanmış tedarikçiler, denetlenmiş kapasite, eksiksiz belge." },
        { i: Globe2, t: "Küresel Erişim", d: "35+ ülke ve büyük limanlarda aktif ağlar." },
        { i: Award, t: "Premium Kalite", d: "Yalnızca A sınıfı emtia ve sertifikalı ürünler." },
        { i: Anchor, t: "Lojistik Ustalığı", d: "Deniz, hava ve çok modlu uçtan uca koordinasyon." },
      ],
    },
    id: {
      eyebrow: "Tentang Nakia",
      title: "Perdagangan Internasional, Dibangun atas Kepercayaan.",
      lead: "Nakia Global Sejahtera menghubungkan Indonesia, Türkiye, Singapura, dan Asia Tenggara dengan pasar global. Kami melayani pembeli B2B, pemasok, dan mitra institusional dengan solusi perdagangan transparan dan patuh regulasi.",
      missionTitle: "Misi Kami",
      mission: "Mengirimkan komoditas premium, terlacak, dan tersertifikasi lintas negara dengan dokumentasi yang dipercaya pembeli institusional dan regulator.",
      visionTitle: "Visi Kami",
      vision: "Menjadi jembatan dagang paling andal antara Asia Tenggara, Timur Tengah, dan Eropa.",
      values: [
        { i: ShieldCheck, t: "Kepercayaan", d: "Pemasok terverifikasi, kapasitas teraudit, dokumentasi lengkap." },
        { i: Globe2, t: "Jangkauan Global", d: "Jaringan aktif di 35+ negara dan pelabuhan utama." },
        { i: Award, t: "Kualitas Premium", d: "Hanya komoditas Grade-A dan barang manufaktur tersertifikasi." },
        { i: Anchor, t: "Penguasaan Logistik", d: "Koordinasi laut, udara, dan multimoda menyeluruh." },
      ],
    },
  }[lang] || {};

  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="bg-[#0A1128] text-white py-28 md:py-36 relative">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              "linear-gradient(180deg, rgba(10,17,40,0.95) 0%, rgba(10,17,40,0.7) 100%), url('https://images.unsplash.com/photo-1758518729706-b1810dd39cc6')",
            backgroundSize: "cover", backgroundPosition: "center",
          }}
        />
        <div className="container-nakia relative">
          <div className="flex items-center gap-3 mb-5">
            <span className="gold-divider" />
            <span className="text-eyebrow text-[#D4AF37]">{content.eyebrow}</span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl max-w-4xl leading-[1.05]">{content.title}</h1>
          <p className="text-white/80 max-w-2xl mt-7 text-lg leading-relaxed">{content.lead}</p>
        </div>
      </section>

      {/* Mission / Vision */}
      <section className="py-24 sm:py-32 bg-white">
        <div className="container-nakia grid grid-cols-1 md:grid-cols-2 gap-12">
          <div>
            <div className="text-eyebrow text-[#D4AF37] mb-4">// 01</div>
            <h2 className="font-display text-3xl sm:text-4xl text-[#0A1128] mb-5">{content.missionTitle}</h2>
            <p className="text-slate-600 leading-relaxed">{content.mission}</p>
          </div>
          <div>
            <div className="text-eyebrow text-[#D4AF37] mb-4">// 02</div>
            <h2 className="font-display text-3xl sm:text-4xl text-[#0A1128] mb-5">{content.visionTitle}</h2>
            <p className="text-slate-600 leading-relaxed">{content.vision}</p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 sm:py-32 bg-[#F8FAFC]">
        <div className="container-nakia">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {(content.values || []).map((v, i) => (
              <div key={i} className="bg-white border border-slate-200 p-8 rounded-sm">
                <div className="w-12 h-12 border border-[#D4AF37]/40 flex items-center justify-center mb-5 rounded-sm">
                  <v.i size={20} className="text-[#D4AF37]" />
                </div>
                <h3 className="font-display text-xl text-[#0A1128] mb-2">{v.t}</h3>
                <p className="text-sm text-slate-600">{v.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership */}
      <Leadership />
    </div>
  );
};

export default About;
